
import { useState, useEffect, useMemo } from "react";
import ViewToggle from "./ViewToggle";
import DeviceGrid from "./DeviceGrid";
import DeviceTable from "./DeviceTable";
import { useDevices } from "@/hooks/useDevices";
import { Loader2 } from "lucide-react";
import { DeviceSearch } from "./dashboard/DeviceSearch";
import { DeviceStatusCards } from "./dashboard/DeviceStatusCards";
import { CardDisplayToggle, type CardDisplayMode } from "./dashboard/CardDisplayToggle";
import { useScrollResponsiveCards } from "@/hooks/useScrollResponsiveCards";
import { getDeviceStatus } from "@/utils/deviceStatus";

interface DeviceListProps {
  statusFilter: string | null;
  onStatusClick: (status: string | null) => void;
}

const DeviceList = ({ statusFilter, onStatusClick }: DeviceListProps) => {
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("deviceListViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "list";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [cardMode, setCardMode] = useState<CardDisplayMode>(() => {
    const savedMode = localStorage.getItem("deviceListCardMode");
    return (savedMode === "full" || savedMode === "compact" || savedMode === "hidden") ? savedMode : "full";
  });
  
  useEffect(() => {
    localStorage.setItem("deviceListViewPreference", view);
  }, [view]);

  useEffect(() => {
    localStorage.setItem("deviceListCardMode", cardMode);
  }, [cardMode]);

  const { currentMode: cardDisplayMode, isSticky, scrollContainerRef, searchBarRef } = useScrollResponsiveCards({
    initialMode: cardMode
  });

  const { data: devices = [], isLoading, error, total, page, totalPages, setPage, deviceTags, statusCounts } = useDevices();
  
  // Phase 5: Use useMemo for expensive computations
  const enrichedDevices = useMemo(() => devices.map(device => {
    // Department and location can be random for now since they're not stored in DB
    return {
      ...device,
      department: device.department || ['Engineering', 'Design', 'Marketing', 'Sales', 'HR', 'Finance'][Math.floor(Math.random() * 6)],
      location: device.location || ['Cupertino HQ', 'New York Office', 'London Office', 'Singapore Office', 'Tokyo Office'][Math.floor(Math.random() * 5)]
    };
  }), [devices]);
  
  // Phase 5: Memoize filtered devices to prevent recalculation on every render
  const filteredDevices = useMemo(() => enrichedDevices.filter((device) => {
    // First check if the device matches the search query
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      (!searchQuery) || // If no search query, include all devices
      (device.name || '').toLowerCase().includes(searchLower) ||
      (device.ip_address || '').toLowerCase().includes(searchLower) ||
      (device.hostname || '').toLowerCase().includes(searchLower) ||
      `${device.os_type || ''} ${device.os_version || ''}`.toLowerCase().includes(searchLower) ||
      (device.serial_number || '').toLowerCase().includes(searchLower) ||
      (device.department || '').toLowerCase().includes(searchLower) ||
      (device.location || '').toLowerCase().includes(searchLower) ||
      (device.customer || '').toLowerCase().includes(searchLower) || // Add search by customer
      (device.tenantId || '').toLowerCase().includes(searchLower);   // Add search by tenantId

    // Then check if the device matches the status filter
    const deviceStatus = getDeviceStatus(device.last_seen || device.created_at, device.status);
    const matchesStatus = !statusFilter || deviceStatus === statusFilter;

    return matchesSearch && matchesStatus;
  }), [enrichedDevices, searchQuery, statusFilter]);

  console.log('Filtered devices count:', filteredDevices.length);
  console.log('Status filter:', statusFilter);
  console.log('Search query:', searchQuery);
  console.log('Sample device customer and tenantId:', filteredDevices.length > 0 ? {
    customer: filteredDevices[0].customer,
    tenantId: filteredDevices[0].tenantId
  } : 'No devices');

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    const isNetworkError = error instanceof TypeError && error.message === 'Failed to fetch';
    const errorMessage = isNetworkError
      ? "Unable to connect to the server. Please check your network connection or try again later."
      : error instanceof Error
        ? error.message
        : "An unexpected error occurred while loading devices.";

    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3">
        <p className="text-destructive font-medium">Unable to load devices</p>
        <p className="text-sm text-muted-foreground max-w-md text-center">{errorMessage}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary hover:underline"
        >
          Reload page
        </button>
      </div>
    );
  }

  if (!devices.length) {
    return (
      <div className="text-center text-gray-500">
        No devices found. Enroll a device to get started.
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className="space-y-4 w-full">
      {!isSticky && (
        <div className="flex items-center justify-end">
          <CardDisplayToggle 
            mode={cardDisplayMode} 
            onModeChange={setCardMode}
          />
        </div>
      )}
      
        <DeviceStatusCards 
          counts={statusCounts} 
          isLoading={isLoading} 
          onStatusClick={onStatusClick} 
          statusFilter={statusFilter}
          mode={cardDisplayMode}
        />
      
      <div
        ref={searchBarRef}
        className={`sticky top-0 z-40 -mx-6 px-6 flex items-center justify-between gap-4 transition-all duration-200 border-b py-4 ${isSticky ? 'bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80 border-border shadow-sm' : 'bg-background border-transparent'}`}
      >
        <div className="flex items-center gap-2 flex-1">
          <DeviceSearch 
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>
        <ViewToggle view={view} onViewChange={setView} />
      </div>
      <div className="w-full overflow-visible">
        {view === "grid" ? (
          <DeviceGrid devices={filteredDevices} />
        ) : (
          <DeviceTable devices={filteredDevices} deviceTags={deviceTags} />
        )}
      </div>
      
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t pt-4">
          <p className="text-sm text-muted-foreground">
            Showing {devices.length} of {total} devices
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceList;
